<template>
    <CHeader fixed with-subheader class="p-0 border-0 main-app-header">
        <!-- Tier 1: Main Header (White) -->
        <div class="header-top-tier bg-white w-100 d-flex align-items-center px-3">
            <CHeaderBrand class="d-flex align-items-center text-decoration-none brand-link-custom" to="/">
                <img src="@/assets/icons/logo.png" height="55" class="mr-3 brand-logo-img" alt="Mae Fah Luang University">
                <div class="brand-text-wrapper d-flex flex-column justify-content-center">
                    <span class="brand-main-title">Buddhist Art Mae Fah Luang University</span>
                    <span class="brand-mobile-title">Buddhist Art MFU</span>
                    <span class="brand-subtitle">{{ $i18n.locale === 'en' ? 'Short Course Program in Buddhist Art, MFU' : 'หลักสูตรระยะสั้น สาขาวิชาพุทธศิลปกรรม มหาวิทยาลัยแม่ฟ้าหลวง' }}</span>
                </div>
            </CHeaderBrand>

            <CHeaderNav class="ml-auto d-flex align-items-center">
                <!-- User Login Icon / Dropdown (Top Right) -->
                <CHeaderNavItem class="px-3" v-if="!isLoggedIn">
                    <button type="button" class="user-login-trigger text-dark d-flex align-items-center border-0 bg-transparent p-0" :aria-label="$i18n.locale === 'en' ? 'Sign in' : 'เข้าสู่ระบบ'" @click="openLoginModal">
                        <CIcon name="cil-user" size="xl" />
                    </button>
                </CHeaderNavItem>
                <CDropdown
                    v-else
                    :show.sync="accountMenu"
                    @keydown.esc.native.stop.prevent="closeAccountMenu"
                    inNav
                    class="c-header-nav-items px-3"
                    placement="bottom-end"
                    add-menu-classes="pt-0 mt-2 shadow-sm rounded-lg premium-dropdown-menu"
                >
                    <template #toggler>
                        <button ref="accountMenuTrigger" type="button" class="user-login-trigger text-dark border-0 bg-transparent p-0" :aria-label="$i18n.locale === 'en' ? 'Account menu' : 'เมนูบัญชี'" aria-haspopup="true" :aria-expanded="String(accountMenu)">
                            <div class="user-login-trigger text-dark d-flex align-items-center" style="cursor: pointer;">
                                <img v-if="userImage" :src="userImage" alt="" class="rounded-circle border" style="width: 35px; height: 35px; object-fit: cover;" />
                                <CIcon v-else name="cil-user" size="xl" />
                            </div>
                        </button>
                    </template>

                    <!-- User Role Links -->
                    <template v-if="userRole === 'customer' || userRole === 'user'">
                        <CDropdownItem @click="handleNavigateTo('/user/profile')" class="font-weight-bold">
                            <CIcon name="cil-user" class="mr-3" /> {{ $i18n.locale === 'en' ? 'Profile' : 'โปรไฟล์' }}
                        </CDropdownItem>
                    </template>
                    <!-- Artist Role Links -->
                    <template v-else-if="userRole === 'artist'">
                        <CDropdownItem @click="handleNavigateTo('/artist/profile')" class="font-weight-bold">
                            <CIcon name="cil-user" class="mr-3" /> {{ $i18n.locale === 'en' ? 'Profile' : 'โปรไฟล์' }}
                        </CDropdownItem>
                    </template>
                    <!-- Admin Role Links -->
                    <template v-else-if="userRole === 'admin'">
                        <CDropdownItem @click="handleNavigateTo('/admin')" class="font-weight-bold">
                            <CIcon name="cil-speedometer" class="mr-3" /> {{ $i18n.locale === 'en' ? 'Dashboard' : 'แดชบอร์ด' }}
                        </CDropdownItem>
                    </template>

                    <CDropdownDivider/>
                    <CDropdownItem @click="logout" class="text-danger font-weight-bold">
                        <CIcon name="cil-lock-locked" class="mr-3" /> {{ $i18n.locale === 'en' ? 'Logout' : 'ออกจากระบบ' }}
                    </CDropdownItem>
                </CDropdown>

                <CHeaderNavItem class="px-3 border-right language-switcher d-md-down-none">
                    <button type="button" class="d-flex align-items-center text-dark border-0 bg-transparent p-0" :aria-label="$i18n.locale === 'th' ? 'Switch to English' : 'เปลี่ยนเป็นภาษาไทย'" @click="toggleLang">
                        <CIcon name="cil-globe-alt" size="lg" class="mr-2" />
                        <span class="font-weight-bold">{{ $i18n.locale === 'th' ? 'EN' : 'TH' }}</span>
                    </button>
                </CHeaderNavItem>
                
                <CHeaderNavItem class="px-3">
                    <button ref="cartTrigger" type="button" class="position-relative cart-trigger text-dark border-0 bg-transparent p-0" :aria-label="$i18n.locale === 'en' ? 'Open cart' : 'เปิดตะกร้าสินค้า'" :aria-expanded="String(isCartOpen)" aria-controls="shopping-cart-panel" @click="setShowCart(true)">
                        <CIcon name="cil-basket" size="xl" />
                        <span v-if="cartCount > 0" class="badge badge-pill badge-danger cart-count-badge">
                            {{ cartCount }}
                        </span>
                    </button>
                </CHeaderNavItem>

                <CHeaderNavItem class="px-2 d-lg-none">
                    <CToggler
                        ref="navigationTrigger"
                        in-header
                        :aria-label="$i18n.locale === 'en' ? 'Open navigation menu' : 'เปิดเมนูนำทาง'"
                        :aria-expanded="String(mobileMenu)"
                        aria-controls="mobile-navigation-panel"
                        @click="mobileMenu = !mobileMenu"
                    >
                        <CIcon name="cil-menu" size="xl" class="text-dark" />
                    </CToggler>
                </CHeaderNavItem>
            </CHeaderNav>
        </div>

        <!-- Tier 2: Subheader (Burgundy Bar) -->
        <div class="header-nav-tier w-100 d-none d-lg-flex" style="height: 50px; background-color: #612721;">
            <CContainer fluid class="d-flex justify-content-center align-items-center h-100">
                <CHeaderNav class="nav-links-container">
                    <CHeaderNavItem class="px-4">
                        <CHeaderNavLink to="/" exact class="nav-link-custom">{{ $t('header.home') }}</CHeaderNavLink>
                    </CHeaderNavItem>
                    <CHeaderNavItem class="px-4">
                        <CHeaderNavLink to="/artworks" class="nav-link-custom">{{ $t('header.artworks') }}</CHeaderNavLink>
                    </CHeaderNavItem>
                    <CHeaderNavItem class="px-4">
                        <CHeaderNavLink to="/news" class="nav-link-custom">{{ $t('header.news') }}</CHeaderNavLink>
                    </CHeaderNavItem>
                    <CHeaderNavItem class="px-4">
                        <CHeaderNavLink to="/contact" class="nav-link-custom">{{ $t('header.contact') }}</CHeaderNavLink>
                    </CHeaderNavItem>
                    <CHeaderNavItem class="px-4">
                        <a href="https://buddhistart.mfu.ac.th/" target="_blank" rel="noopener noreferrer" class="c-header-nav-link nav-link-custom">
                            {{ $t('header.about') }}
                            <CIcon name="cil-external-link" size="sm" class="ml-1" style="vertical-align: middle; opacity: 0.8; width: 12px; height: 12px;" />
                        </a>
                    </CHeaderNavItem>
                </CHeaderNav>
            </CContainer>
        </div>

        <!-- Mobile Navigation Overlay (Moved outside for cleaner DOM but stays in component) -->
        <transition name="fade">
            <div v-if="mobileMenu" class="mobile-nav-overlay d-lg-none" @click="mobileMenu = false">
                <div id="mobile-navigation-panel" ref="navigationPanel" role="dialog" aria-modal="true" aria-labelledby="mobile-navigation-title" tabindex="-1" class="mobile-menu-content bg-white p-4 h-100" @click.stop @keydown="handleDrawerKeydown($event, 'navigation')">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                        <h5 id="mobile-navigation-title" class="m-0 font-weight-bold">{{ $i18n.locale === 'en' ? 'Menu' : 'เมนู' }}</h5>
                        <button type="button" class="close" :aria-label="$i18n.locale === 'en' ? 'Close navigation menu' : 'ปิดเมนูนำทาง'" @click="mobileMenu = false">
                            <CIcon name="cil-x" size="lg" />
                        </button>
                    </div>
                    <ul class="list-unstyled">
                        <li class="mb-4">
                            <button type="button" class="text-dark h5 p-0 border-0 bg-transparent" @click="toggleLang">
                                {{ $i18n.locale === 'th' ? 'Switch to English' : 'เปลี่ยนเป็นภาษาไทย' }}
                            </button>
                        </li>
                        <li class="mb-4">
                            <CHeaderNavLink to="/" exact class="text-dark h5 p-0" @click.native="mobileMenu = false">{{ $t('header.home') }}</CHeaderNavLink>
                        </li>
                        <li class="mb-4">
                            <CHeaderNavLink to="/artworks" class="text-dark h5 p-0" @click.native="mobileMenu = false">{{ $t('header.artworks') }}</CHeaderNavLink>
                        </li>
                        <li class="mb-4">
                            <CHeaderNavLink to="/news" class="text-dark h5 p-0" @click.native="mobileMenu = false">{{ $t('header.news') }}</CHeaderNavLink>
                        </li>
                        <li class="mb-4">
                            <CHeaderNavLink to="/contact" class="text-dark h5 p-0" @click.native="mobileMenu = false">{{ $t('header.contact') }}</CHeaderNavLink>
                        </li>
                        <li class="mb-4">
                            <a href="https://buddhistart.mfu.ac.th/" target="_blank" rel="noopener noreferrer" class="text-dark h5 p-0 d-block text-decoration-none" @click="mobileMenu = false">
                                {{ $t('header.about') }}
                                <CIcon name="cil-external-link" size="sm" class="ml-1" style="vertical-align: middle; opacity: 0.8; width: 14px; height: 14px;" />
                            </a>
                        </li>

                    </ul>
                </div>
            </div>
        </transition>

        <!-- Cart Sidebar Overlay/Content -->
        <div class="cart-backdrop" v-if="isCartOpen" @click="setShowCart(false)"></div>
        <div id="shopping-cart-panel" ref="cartPanel" role="dialog" :aria-modal="isCartOpen ? 'true' : null" aria-labelledby="shopping-cart-title" tabindex="-1" class="cart-sidebar bg-white shadow-lg" :class="{ 'open': isCartOpen }" :aria-hidden="String(!isCartOpen)" :inert="isCartOpen ? null : ''" @keydown="handleDrawerKeydown($event, 'cart')">
            <div class="d-flex justify-content-between align-items-center p-4 border-bottom">
                <div class="d-flex align-items-center">
                    <h5 id="shopping-cart-title" class="font-weight-bold m-0">{{ $i18n.locale === 'en' ? 'Your Cart' : 'ตะกร้าสินค้า' }} ({{ cartCount || 0 }})</h5>
                    <CIcon v-if="isSyncing" name="cil-reload" size="sm" class="ml-2 text-muted spinner-animation" />
                </div>
                <button type="button" class="close" :aria-label="$i18n.locale === 'en' ? 'Close cart' : 'ปิดตะกร้าสินค้า'" @click="setShowCart(false)">
                    <span aria-hidden="true" style="font-size: 1.5rem;">&times;</span>
                </button>
            </div>

            <div v-if="!cartItems || cartItems.length === 0"
                class="cart-body d-flex flex-column align-items-center justify-content-center h-100 p-4 text-center">
                <div class="mb-4">
                    <CIcon name="cil-basket" size="xl" class="text-muted" style="transform: scale(2);" />
                </div>
                <h5 class="font-weight-bold mb-2">{{ $i18n.locale === 'en' ? 'Cart is empty' : 'ตะกร้าสินค้าว่างเปล่า' }}</h5>
                <CButton color="danger" shape="pill" class="mt-3 px-4 font-weight-bold" @click="startShopping">
                    {{ $i18n.locale === 'en' ? 'Shop Now' : 'ช้อปเลย' }}
                </CButton>
            </div>

            <div v-else class="cart-body d-flex flex-column h-100">
                <div class="flex-grow-1 overflow-auto p-3">
                    <div v-for="item in cartItems" :key="item._id"
                        class="d-flex align-items-center mb-3 pb-3 border-bottom">
                        <div class="mr-3 cart-item-image-wrapper"
                            style="width: 70px; height: 70px; border-radius: 8px; position: relative; overflow: hidden; border: 1px solid #ddd; background: #f0f0f0;">
                            <div class="cart-item-image" 
                                :style="{ 
                                    backgroundImage: 'url(' + String.fromCharCode(34) + item.image + String.fromCharCode(34) + ')', 
                                    filter: isItemReserved(item) ? 'grayscale(1) contrast(0.4)' : 'none', 
                                    opacity: isItemReserved(item) ? 0.5 : 1 
                                }"
                                style="width: 100%; height: 100%; background-size: cover; background-position: center; transition: all 0.3s ease;">
                            </div>
                            <!-- HIGH VISIBILITY RESERVED OVERLAY -->
                            <div v-if="isItemReserved(item)" 
                                class="position-absolute w-100 h-100 d-flex flex-column align-items-center justify-content-center" 
                                style="top:0; left:0; background: rgba(229, 91, 91, 0.4); backdrop-filter: blur(1px);">
                                <CIcon name="cil-lock-locked" class="text-white mb-1" size="sm" />
                                <span style="font-size: 0.55rem; font-weight: 900; color: white; text-transform: uppercase; letter-spacing: 0.8px; text-shadow: 1px 1px 2px rgba(0,0,0,0.5);">
                                    {{ $i18n.locale === 'en' ? 'Reserved' : 'ถูกจองแล้ว' }}
                                </span>
                            </div>
                        </div>
                        <div class="flex-grow-1" :style="{ opacity: isItemReserved(item) ? 0.6 : 1, filter: isItemReserved(item) ? 'grayscale(0.5)' : 'none' }">
                            <h6 class="font-weight-bold mb-0">{{ getName($i18n.locale, item.title) }}</h6>
                            <div class="text-muted small mb-1" v-if="item.artist">{{ $i18n.locale === 'th' ? 'โดย' : 'By' }} {{ getName($i18n.locale, item.artist) }}</div>
                            <div class="font-weight-bold text-danger h6 mb-0">฿{{ formatPrice(item.price) }}</div>
                            <div v-if="isItemReserved(item)" class="text-danger small mt-1 font-weight-bold">
                                <CIcon name="cil-warning" size="sm" class="mr-1"/> 
                                {{ $i18n.locale === 'en' ? 'This item is reserved' : 'สินค้านี้ถูกจองแล้ว' }}
                            </div>
                        </div>
                        <div class="text-right">
                            <CButton size="sm" color="link" class="text-danger" :aria-label="($i18n.locale === 'en' ? 'Remove from cart: ' : 'นำออกจากตะกร้า: ') + getName($i18n.locale, item.title)" @click="removeFromCart(item._id)">
                                <CIcon name="cil-trash" />
                            </CButton>
                        </div>
                    </div>
                </div>
                <div class="p-4 bg-light border-top pb-5 mb-5">
                    <div class="d-flex justify-content-between mb-3 text-dark align-items-center">
                        <span class="h6 mb-0 font-weight-bold">{{ $i18n.locale === 'en' ? 'Total' : 'ยอดรวม' }}</span>
                        <span class="font-weight-bold h4 mb-0 text-danger">฿{{ (availableTotal || 0).toLocaleString() }}</span>
                    </div>
                    <div v-if="hasReservedItems" class="alert alert-warning small py-2 mb-3">
                        {{ $i18n.locale === 'en' ? 'Some items are reserved. Please remove them to proceed.' : 'มีสินค้าบางชิ้นถูกจองแล้ว กรุณานำออกเพื่อดำเนินการต่อ' }}
                    </div>
                    <!-- Checkout Button Area -->
                    <div class="mt-2">
                        <CButton block size="lg" class="py-3 font-weight-bold"
                            :color="hasReservedItems ? 'secondary' : 'danger'"
                            :disabled="hasReservedItems"
                            :style="hasReservedItems ? 'background-color: #cbd5e0; border: none; cursor: not-allowed;' : 'background-color: #E55B5B; border: none;'"
                            @click="handleCheckout">
                            {{ hasReservedItems 
                                ? ($i18n.locale === 'en' ? 'Remove Reserved Items' : 'กรุณาลบรายการที่ถูกจองออก') 
                                : ($i18n.locale === 'en' ? 'Checkout' : 'สั่งซื้อสินค้า') 
                            }}
                        </CButton>
                        <p v-if="hasReservedItems" class="text-center small text-danger mt-2">
                            {{ $i18n.locale === 'en' ? 'Some items are no longer available.' : 'มีบางรายการไม่พร้อมจำหน่ายในขณะนี้' }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </CHeader>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { trapFocusTab } from '@/utils/modalKeyboard';

export default {
    name: 'TheHeaderUser',
    components: {
    },
    data() {
        return {
            mobileMenu: false,
            accountMenu: false,
            isLoggedIn: false,
            userRole: '',
            userImage: '',
            isSyncing: false,
            syncTimer: null
        }
    },
    computed: {
        ...mapGetters('cart', ['cartItems', 'cartCount', 'cartTotal', 'isCartOpen']),
        hasReservedItems() {
            return this.cartItems.some(item => this.isItemReserved(item));
        },
        availableTotal() {
            if (!this.cartItems) return 0;
            return this.cartItems.reduce((total, item) => {
                if (this.isItemReserved(item)) return total;
                const price = typeof item.price === 'string' 
                    ? parseFloat(item.price.replace(/[^0-9.-]+/g, "")) || 0 
                    : (item.price || 0);
                return total + price;
            }, 0);
        },
        userId() {
            return this.$store.getters['auth/userId'];
        }
    },
    watch: {
        cartItems() {
            this.$nextTick(() => {
                const panel = this.$refs.cartPanel;
                if (this.isCartOpen && panel && !panel.contains(document.activeElement)) panel.focus();
            });
        },
        mobileMenu(value, previous) {
            if (value) {
                this.accountMenu = false;
                if (this.isCartOpen) this.setShowCart(false);
                this.focusDrawer('navigation');
            } else if (previous) this.restoreDrawerTrigger('navigation');
        },
        $route() {
            this.checkLoginStatus();
        },
        userId() {
            this.checkLoginStatus();
        },
        isCartOpen: {
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal) {
                    this.mobileMenu = false;
                    this.accountMenu = false;
                    this.focusDrawer('cart');
                    this.refreshCartStatuses();
                    // Balanced sync every 5 seconds while cart is open
                    this.syncTimer = setInterval(() => {
                        this.refreshCartStatuses(true); 
                    }, 5000);
                } else {
                    if (this.syncTimer) {
                        clearInterval(this.syncTimer);
                        this.syncTimer = null;
                    }
                    if (oldVal) this.restoreDrawerTrigger('cart');
                }
            }
        }
    },
    created() {
        this.checkLoginStatus();
    },
    beforeDestroy() {
        if (this.syncTimer) clearInterval(this.syncTimer);
    },
    methods: {
        focusDrawer(kind) {
            this.$nextTick(() => {
                if (kind === 'cart' ? !this.isCartOpen : !this.mobileMenu) return;
                const panel = this.$refs[`${kind}Panel`];
                if (panel) panel.focus();
            });
        },
        restoreDrawerTrigger(kind) {
            this.$nextTick(() => {
                if (this.isCartOpen || this.mobileMenu) return;
                const ref = this.$refs[`${kind}Trigger`];
                const trigger = ref && (ref.$el || ref);
                if (trigger && trigger.isConnected) trigger.focus();
            });
        },
        handleDrawerKeydown(event, kind) {
            if (event.key === 'Tab') trapFocusTab(event, this.$refs[`${kind}Panel`]);
            else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                if (kind === 'cart') this.setShowCart(false);
                else this.mobileMenu = false;
            }
        },
        closeAccountMenu() {
            this.accountMenu = false;
            this.$nextTick(() => this.$refs.accountMenuTrigger && this.$refs.accountMenuTrigger.focus());
        },
        ...mapActions('cart', ['removeFromCart', 'setShowCart', 'addToCart']),
        async refreshCartStatuses(silent = false) {
            if (!this.cartItems || this.cartItems.length === 0) return;
            
            if (!silent) this.isSyncing = true;
            try {
                const promises = this.cartItems.map(item => 
                    this.$store.dispatch('artwork/explore', { _id: item._id })
                );
                
                const results = await Promise.all(promises);
                
                results.forEach((latest, index) => {
                    if (latest) {
                        const artwork = Array.isArray(latest) ? latest[0] : latest;
                        if (artwork) {
                            // Deep sync status to Vuex
                            this.$store.dispatch('cart/updateCartItem', artwork);
                        }
                    }
                });
            } catch (error) {
                console.error('Error refreshing cart statuses:', error);
            } finally {
                if (!silent) this.isSyncing = false;
            }
        },
        isItemReserved(item) {
            if (!item || !item.status) return false;
            
            // Check Approval Status
            if (item.status.approval) {
                const appEn = this.getName('en', item.status.approval);
                const appTh = this.getName('th', item.status.approval);
                if (appEn === 'Reserved' || appTh === 'รอชำระเงิน') return true;
            }

            // Check Sale Status
            if (item.status.sale) {
                const saleEn = this.getName('en', item.status.sale);
                const saleTh = this.getName('th', item.status.sale);
                // "Sold Out" or "Reserved" (if it existed in sale group)
                if (saleEn === 'Sold Out' || saleTh === 'ขายแล้ว' || saleEn === 'Reserved' || saleTh === 'รอชำระเงิน') return true;
            }

            return false;
        },
        async checkLoginStatus() {
            try {
                const objs = JSON.parse(localStorage.getItem('objs'));
                if (objs && objs.xAccessToken) {
                    this.isLoggedIn = true;
                    this.userRole = (objs.role || 'customer').toLowerCase();
                    const userId = objs.accounts;
                    if (userId) {
                        try {
                            const users = await this.$store.dispatch('user/get', {});
                            const currentUser = users ? users.find(u => u._id === userId) : null;
                            this.userImage = currentUser && currentUser.image || '';
                        } catch (error) {
                            // Profile loading does not determine whether a saved session exists.
                            // Authentication failures remain enforced by the API interceptor.
                        }
                    } else {
                        this.userImage = '';
                    }
                } else {
                    this.isLoggedIn = false;
                    this.userRole = '';
                    this.userImage = '';
                }
            } catch (e) {
                this.isLoggedIn = false;
                this.userRole = '';
                this.userImage = '';
            }
        },
        logout() {
            localStorage.removeItem('objs');
            this.checkLoginStatus();
            this.$store.commit('auth/showSignInModal', false);
            window.location.href = '/';
        },
        handleCheckout() {
            if (!this.userId) {
                this.$store.commit('dialog/showToast', {
                    title: 'Authentication Required',
                    message: this.$i18n.locale === 'en' ? 'Please login before checking out' : 'กรุณาเข้าสู่ระบบก่อนสั่งซื้อสินค้า',
                    variant: 'warning'
                });
                this.$store.commit('auth/postLoginContext', { redirect: '/payment', reopenCart: false });
                this.openLoginModal();
                this.setShowCart(false);
                return;
            }
            this.handleNavigateTo('/payment');
            this.setShowCart(false);
        },
        startShopping() {
            this.setShowCart(false);
            this.handleNavigateTo('/artworks');
        },
        formatPrice(value) {
            if (value === undefined || value === null) return '0';
            return parseFloat(value).toLocaleString('en-US');
        },
        openLoginModal() {
            this.$store.commit('auth/showSignInModal', true);
        },
        toggleLang() {
            const newLang = this.$i18n.locale === 'th' ? 'en' : 'th';
            this.$i18n.locale = newLang;
            this.$store.commit("setting/lang", newLang);
            localStorage.setItem('lang', newLang);
        },
        handleNavigateTo(path) {
            if (this.$route.path !== path) {
                return this.$router.push(path).catch(err => {
                    if (err && err.name !== 'NavigationDuplicated') {
                        throw err;
                    }
                });
            }
        }
    }
}
</script>

<style scoped>
/* Reset CHeader for custom branding height */
.main-app-header {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: auto !important;
    min-height: auto !important;
    display: flex !important;
    flex-direction: column !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
    z-index: 1030 !important;
}

.header-top-tier {
    position: relative;
    z-index: 1030;
    height: 75px;
}

.c-header-brand.brand-link-custom {
    flex: 0 1 auto;
    justify-content: flex-start !important;
    min-width: 0;
    margin-right: 1rem;
    text-decoration: none !important;
    color: inherit !important;
}

.brand-text-wrapper {
    min-width: 0;
}

.brand-main-title {
    color: #3c4b64 !important; /* Charcoal */
    text-decoration: none !important;
    font-weight: 800;
    font-size: 1.4rem;
    line-height: 1;
    display: inline-block;
}

.brand-mobile-title {
    display: none;
    color: #3c4b64;
    font-weight: 800;
    line-height: 1.1;
}

.brand-subtitle {
    color: #612721 !important; /* Burgundy */
    text-decoration: none !important;
    font-weight: 700;
    font-size: 0.95rem;
    margin-top: 4px;
    line-height: 1.35;
}

.brand-logo-img {
    flex: 0 0 auto;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

.header-top-tier > .c-header-nav {
    flex: 0 0 auto;
    flex-wrap: nowrap;
}

/* Burgundy Navigation Bar */
.header-nav-tier {
    z-index: 1020;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
}

.nav-link-custom {
    color: rgba(255, 255, 255, 0.9) !important;
    font-weight: 600 !important;
    font-size: 1.05rem !important;
    padding: 0 !important;
    position: relative;
    transition: all 0.3s ease;
}

.nav-link-custom:hover {
    color: #f0c24d !important; 
    text-decoration: none !important;
}

.nav-link-custom.c-active {
    color: #ffffff !important;
}

.nav-link-custom::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 0;
    height: 3px;
    background-color: #f0c24d;
    border-radius: 2px;
    transition: all 0.3s ease;
    opacity: 0;
}

.nav-link-custom:hover::after, .nav-link-custom.c-active::after {
    width: 100%;
    opacity: 1;
}

.nav-link-custom.c-active::after {
    background-color: #ffffff;
}

/* Utility styles */
.user-login-trigger {
    transition: all 0.2s ease;
    opacity: 0.8;
}

.user-login-trigger:hover {
    transform: scale(1.1);
    color: #612721 !important;
    opacity: 1;
}

.cart-trigger {
    transition: transform 0.2s ease;
}

.cart-trigger:hover {
    transform: scale(1.1);
}

.cart-count-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #612721;
    font-size: 0.75rem;
    padding: 3px 6px;
}

.cart-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 2040;
    backdrop-filter: blur(2px);
}

.cart-sidebar {
    position: fixed;
    top: 0;
    right: -400px;
    width: 400px;
    height: 100vh;
    z-index: 2050;
    transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.cart-sidebar.open {
    right: 0;
}

.mobile-nav-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.5);
    z-index: 3000;
}

.mobile-menu-content {
    width: 300px;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

@media (max-width: 991.98px) {
    .c-header-brand.brand-link-custom {
        margin-right: 0.5rem;
    }

    .brand-main-title {
        font-size: 1.25rem;
    }

    .brand-logo-img {
        height: 55px;
    }
}

@media (max-width: 767.98px) {
    .header-top-tier {
        height: 72px;
        padding-right: 8px !important;
        padding-left: 12px !important;
    }

    .brand-main-title {
        display: none;
    }

    .brand-mobile-title {
        display: block;
        overflow: hidden;
        font-size: 0.95rem;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .brand-subtitle {
        display: -webkit-box;
        overflow: hidden;
        margin-top: 1px;
        padding-top: 3px;
        padding-bottom: 2px;
        font-size: 0.68rem;
        line-height: 1.35;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
    }

    .brand-logo-img {
        width: auto;
        height: 44px;
        margin-right: 8px !important;
    }

    .header-top-tier > .c-header-nav > .c-header-nav-item,
    .header-top-tier > .c-header-nav > .c-header-nav-items {
        padding-right: 4px !important;
        padding-left: 4px !important;
    }

    .header-top-tier > .c-header-nav .user-login-trigger,
    .header-top-tier > .c-header-nav .cart-trigger,
    .header-top-tier > .c-header-nav .c-header-toggler {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 44px;
        min-height: 44px;
    }
}

@media (max-width: 359.98px) {
    .brand-mobile-title {
        max-width: 92px;
        font-size: 0.85rem;
    }

    .header-top-tier > .c-header-nav > .c-header-nav-item,
    .header-top-tier > .c-header-nav > .c-header-nav-items {
        padding-right: 0 !important;
        padding-left: 0 !important;
    }
}
</style>
